import { AlfredFiatAccountType } from "src/api";

/**
 * Per-rail validation rules for Alfred fiatAccount registration.
 *
 * Source of truth: https://alfredpay.readme.io/docs/registering-payment-methods
 * Plus 2026-05 live probes against Alfred's dev sandbox for fields the
 * docs miss (`phoneNumber`/`pep`/`cuit` for AR; `phoneNumber` for CO). Keep this table in sync with the BE constraint in
 * vakotrade `dto/alfred-fiat-account-fields.validator.ts` — they share
 * intent (Alfred is the only authority, but a local mirror catches
 * obvious mistakes before a round-trip and gives field-level UX).
 */
/** A translatable validation error: an i18n key, an English fallback for
 *  `defaultValue`, and optional interpolation params. The form resolves it
 *  with `t(key, { defaultValue, ...params })`. */
export interface FieldError {
  key: string;
  default: string;
  params?: Record<string, unknown>;
}

export interface RuleDef {
  /** Path inside `fiatAccountFields` */
  key: string;
  label: string;
  required: boolean;
  placeholder?: string;
  /** Validation predicates run on submit and onBlur. Return error or null.
   *  `values` is the sibling field object, so a rule can validate one field
   *  against another (e.g. PIX accountNumber format depends on accountType). */
  validate?: (value: string, values: Record<string, unknown>) => FieldError | null;
  /** Type hint for the input component (default: text). */
  inputType?: "text" | "checkbox";
  /** Closed set of allowed values — when present the form renders a
   *  dropdown instead of a free-text field, so the user can't typo the
   *  enum (Alfred rejects unknown accountTypes etc. with 110002). */
  options?: string[];
  /** Like `options`, but for fields where the submitted value isn't fit
   *  to display (e.g. a bank code) — `key` is what gets stored/submitted,
   *  `label` is what the user sees in the dropdown. Unlike `options`,
   *  there's no `oneOf` enum enforced by Alfred here — this is just a
   *  curated shortlist to spare the user a lookup, not the full registry. */
  selectOptions?: { key: string; label: string }[];
  /** Muted helper line rendered below the input. Use it to explain a
   *  non-obvious constraint (e.g. bank name must match Alfred's
   *  registry) — saves a 110002 round-trip the user can't otherwise
   *  resolve from the label alone. */
  hint?: string;
}

const digits = (n: number) => (v: string): FieldError | null =>
  new RegExp(`^\\d{${n}}$`).test(v)
    ? null
    : { key: "ALFRED.VALIDATION.DIGITS_EXACT", default: `Must be exactly ${n} digits`, params: { n } };

const digitsRange = (min: number, max: number) => (v: string): FieldError | null =>
  new RegExp(`^\\d{${min},${max}}$`).test(v)
    ? null
    : {
        key: "ALFRED.VALIDATION.DIGITS_RANGE",
        default: `Must be ${min}–${max} digits`,
        params: { minDigits: min, maxDigits: max },
      };

const oneOf = (allowed: string[]) => (v: string): FieldError | null =>
  allowed.includes(v.toUpperCase())
    ? null
    : { key: "ALFRED.VALIDATION.ONE_OF", default: `Must be one of: ${allowed.join(", ")}`, params: { values: allowed.join(", ") } };

const aliasPattern = (v: string): FieldError | null =>
  /^[a-zA-Z0-9.\-]{6,20}$/.test(v)
    ? null
    : { key: "ALFRED.VALIDATION.ALIAS", default: "6–20 chars, letters/digits/dot/dash only" };

// Alfred has no bank registry for BR (its `/bank-list/{country}` endpoint,
// used for CO's PSE onramp picker, returns `[]` for BR — confirmed live
// 2026-07-23). `accountBankCode` is optional metadata only; the PIX key
// itself routes the payout. This is a curated shortlist (top ~15 by PIX
// market share), not the full ~250-entry Bacen COMPE table — the free-text
// fallback (blank + type it yourself) still exists for any bank not listed.
const BR_BANKS: { key: string; label: string }[] = [
  { key: "001", label: "Banco do Brasil" },
  { key: "033", label: "Santander" },
  { key: "077", label: "Banco Inter" },
  { key: "104", label: "Caixa Econômica Federal" },
  { key: "208", label: "Banco BTG Pactual" },
  { key: "212", label: "Banco Original" },
  { key: "237", label: "Bradesco" },
  { key: "260", label: "Nu Pagamentos (Nubank)" },
  { key: "290", label: "PagBank (PagSeguro)" },
  { key: "323", label: "Mercado Pago" },
  { key: "336", label: "Banco C6" },
  { key: "341", label: "Itaú Unibanco" },
  { key: "380", label: "PicPay" },
  { key: "422", label: "Banco Safra" },
  { key: "748", label: "Sicredi" },
  { key: "756", label: "Sicoob" },
];

// Brazil PIX key types accepted by Alfred's penny adapter. Mirrors the BE constraint.
const PIX_KEY_TYPES = ["CPF", "CNPJ", "EMAIL", "PHONE", "EVP"];
// The key value format depends on the chosen type. Stay lenient — Alfred is
// the authority; this only fail-fasts an obvious wrong-type paste.
const pixKey = (v: string, values: Record<string, unknown>): FieldError | null => {
  switch (String(values?.accountType ?? "").toUpperCase()) {
    case "CPF":
      return digits(11)(v);
    case "CNPJ":
      return digits(14)(v);
    case "EMAIL":
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)
        ? null
        : { key: "ALFRED.VALIDATION.EMAIL", default: "Must be a valid email" };
    case "PHONE":
      return /^\+?\d{10,15}$/.test(v)
        ? null
        : { key: "ALFRED.VALIDATION.PHONE", default: "Must be a phone number (10–15 digits, optional +)" };
    // EVP (chave aleatória) — Alfred issues a UUID; leave the shape to Alfred.
    default:
      return null;
  }
};

export const RAIL_RULES: Record<AlfredFiatAccountType, RuleDef[]> = {
  PIX: [
    { key: "accountNumber", label: "PIX key", required: true, placeholder: "CPF, CNPJ, email, phone or random key", validate: pixKey },
    { key: "accountType", label: "PIX key type", required: true, options: PIX_KEY_TYPES, validate: oneOf(PIX_KEY_TYPES) },
    { key: "accountName", label: "Account holder", required: false, placeholder: "Full name on the bank account" },
    {
      key: "accountBankCode",
      label: "Bank",
      required: false,
      placeholder: "e.g. 341",
      selectOptions: BR_BANKS,
      hint: "Top banks only — leave blank if yours isn't listed",
    },
  ],
  SPEI: [
    { key: "accountNumber", label: "CLABE", required: true, placeholder: "18 digits", validate: digits(18) },
    { key: "accountType", label: "Account type", required: false, options: ["CLABE"], validate: oneOf(["CLABE"]) },
    { key: "accountName", label: "Account holder", required: false, placeholder: "Full name on the bank account" },
  ],
  COELSA: [
    // NB: `cuit/phoneNumber/pep` belong to the KYC submission, not the
    // fiatAccount payload — Alfred returns 111302 "Invalid parameter(s)
    // cuit, phoneNumber, pep" if we send them here. Alfred autopopulates
    // bankName/holderName/bankCode/cuit on the response from accountNumber.
    { key: "accountNumber", label: "CBU / CVU", required: true, placeholder: "22 digits", validate: digits(22) },
    { key: "accountType", label: "Account type", required: false, options: ["CBU", "CVU", "ALIAS"], validate: oneOf(["CBU", "CVU", "ALIAS"]) },
    { key: "accountAlias", label: "Alias", required: false, placeholder: "alphanum/dash/dot, 6–20", validate: (v) => (v ? aliasPattern(v) : null) },
  ],
  ACH: [
    // Colombia ACH. Alfred requires `accountName` (the bank name, e.g.
    // "Bancamia") — the doc shows it as a hint but the API treats it as
    // required. `phoneNumber` is KYC-only and rejected here (Alfred 111302).
    { key: "accountNumber", label: "Account number", required: true, placeholder: "10–11 digits", validate: digitsRange(10, 11) },
    { key: "accountType", label: "Account type", required: true, options: ["AHORRO", "CORRIENTE"], validate: oneOf(["AHORRO", "CORRIENTE"]) },
    { key: "accountName", label: "Bank name", required: true, placeholder: "e.g. Bancolombia", hint: "Must match a Colombian bank registered with Alfred. Examples: Bancolombia, Davivienda, BBVA, Banco de Bogotá, Bancamia." },
  ],
  ACH_DOM: [
    // Dominican ACH. Same shape as Colombia ACH: `accountName` is the
    // BANK NAME (Alfred matches against its own bank registry, e.g.
    // typing "BANCO POPULAR" resolves to "BANCO POPULAR DOMINICANO
    // BANCO MULTIPLE, S.A." server-side). Free-text holder names are
    // rejected with 110002.
    { key: "accountNumber", label: "Account number", required: true, placeholder: "Bank account number" },
    { key: "accountType", label: "Account type", required: true, options: ["AHORRO", "CORRIENTE"], validate: oneOf(["AHORRO", "CORRIENTE"]) },
    { key: "accountName", label: "Bank name", required: true, placeholder: "e.g. BANCO POPULAR", hint: "Must match a Dominican bank registered with Alfred. Examples: BANCO POPULAR, BANRESERVAS, BANCO BHD." },
  ],
  BANK_USA: [
    { key: "accountNumber", label: "Account number", required: true, placeholder: "Bank account number" },
    { key: "accountType", label: "Account type", required: true, options: ["CHECKING", "SAVINGS"], validate: oneOf(["CHECKING", "SAVINGS"]) },
    { key: "routingNumber", label: "Routing number (ABA)", required: true, placeholder: "9 digits", validate: digits(9) },
    { key: "bankStreet", label: "Bank street", required: true, placeholder: "Bank branch street address" },
    { key: "bankCity", label: "Bank city", required: true, placeholder: "Bank branch city" },
    { key: "bankState", label: "Bank state", required: true, placeholder: "e.g. NY" },
    { key: "bankPostalCode", label: "Bank ZIP", required: true, placeholder: "5-digit ZIP" },
  ],
  BANK_CN: [
    { key: "accountNumber", label: "Account number", required: true, placeholder: "Bank account number" },
    { key: "accountType", label: "Account type", required: true, options: ["CHECKING", "SAVINGS"], validate: oneOf(["CHECKING", "SAVINGS"]) },
    { key: "networkIdentifier", label: "SWIFT", required: true, placeholder: "e.g. ABOCCNBJ" },
    { key: "accountName", label: "Account holder", required: true, placeholder: "Full name on the bank account" },
    { key: "bankStreet", label: "Bank street", required: true, placeholder: "Bank branch street address" },
    { key: "bankCity", label: "Bank city", required: true, placeholder: "Bank branch city" },
    { key: "bankState", label: "Bank state", required: true, placeholder: "Province / region" },
    { key: "bankPostalCode", label: "Bank postal code", required: true, placeholder: "6-digit postal code" },
  ],
  ACH_CHL: [
    { key: "accountNumber", label: "Account number", required: true, placeholder: "Bank account number" },
    { key: "accountType", label: "Account type", required: true, options: ["CUENTA_CORRIENTE", "CUENTA_VISTA"], validate: oneOf(["CUENTA_CORRIENTE", "CUENTA_VISTA"]) },
  ],
  ACH_BOL: [
    { key: "accountNumber", label: "Account number", required: true, placeholder: "Bank account number" },
    { key: "accountType", label: "Account type", required: true, options: ["AHORRO", "CORRIENTE"], validate: oneOf(["AHORRO", "CORRIENTE"]) },
  ],
  B89: [
    { key: "accountNumber", label: "Account number", required: true, placeholder: "20-digit CCI" },
    { key: "accountType", label: "Account type", required: true, options: ["AHORRO", "CORRIENTE"], validate: oneOf(["AHORRO", "CORRIENTE"]) },
  ],
};

/** Per-field error map, used by both the form and submit gating. */
export function validateFiatAccountFields(
  type: AlfredFiatAccountType,
  values: Record<string, unknown>,
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {};
  const rules = RAIL_RULES[type] ?? [];
  for (const r of rules) {
    const raw = values[r.key];
    const isCheckbox = r.inputType === "checkbox";
    const v = isCheckbox ? (raw === true ? "true" : raw === false ? "false" : "") : (typeof raw === "string" ? raw : "");
    if (!v) {
      if (r.required) errors[r.key] = { key: "ALFRED.VALIDATION.REQUIRED", default: "Required" };
      continue;
    }
    if (r.validate && !isCheckbox) {
      const msg = r.validate(v, values);
      if (msg) errors[r.key] = msg;
    }
  }
  return errors;
}
